import { useContext } from 'react';
import { XIcon } from '@page/icons';
import { Slot } from '@radix-ui/react-slot';

import { IconButton } from '../IconButton';
import { cn } from '../../lib/styles';
import { DialogContext } from '../Dialog/DialogContext';

interface DialogTitleProps {
  children: React.ReactNode;
  description?: React.ReactNode;
  closeButton?: React.ReactNode;
  className?: string;
  headingClassName?: string;
  headerClassName?: string;
  onClose?: () => void;
  suffix?: React.ReactNode;
  headingPrefix?: React.ReactNode;
  bottomContent?: React.ReactNode;
}

type DialogActionsProps = React.HTMLAttributes<HTMLDivElement>;

export interface DialogContentProps {
  children: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

interface DialogCloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  asChild?: boolean;
}

export function DialogClose({ asChild = true, children, onClick, ...rest }: DialogCloseProps) {
  const dialogContext = useContext(DialogContext);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    dialogContext?.close();
  };

  const Comp = asChild ? Slot : 'button';

  return (
    <Comp type="button" data-slot="dialog-close" onClick={handleClick} {...rest}>
      {children}
    </Comp>
  );
}

export const DialogTitle = ({
  children,
  closeButton: closeButtonProp,
  description,
  onClose,
  className,
  headingClassName,
  headerClassName,
  headingPrefix,
  suffix,
  bottomContent,
}: DialogTitleProps) => {
  const h2Element = <h2 className="font-heading-xl font-semibold text-text-primary text-nowrap">{children}</h2>;

  const renderCloseButton = () => {
    if (typeof closeButtonProp !== 'undefined') return closeButtonProp;

    const button = (
      <IconButton onClick={onClose} aria-label="Close">
        <XIcon className="size-5" fill="currentColor" />
      </IconButton>
    );

    if (onClose) return button;

    return <DialogClose asChild>{button}</DialogClose>;
  };

  const closeButton = renderCloseButton();

  const header = (
    <div className={cn('flex items-center justify-between gap-4 h-6', headerClassName)}>
      <div className={cn('flex items-center gap-2', headingClassName)}>
        {headingPrefix}
        {h2Element}
      </div>

      {suffix}

      {closeButton}
    </div>
  );

  const descriptionElement = description ? (
    <p className="font-body-sm font-medium text-text-tertiary pr-10">{description}</p>
  ) : null;

  const content = (
    <>
      {header}
      {descriptionElement}
      {bottomContent}
    </>
  );

  return (
    <div
      data-slot="dialog-title"
      className={cn('flex flex-col gap-2 py-5 pl-5 pr-2.5 border-b border-border-default', className)}
    >
      {content}
    </div>
  );
};

export const DialogFooter = ({ children, className, ...rest }: DialogActionsProps) => {
  return (
    <div
      className={cn('flex items-center justify-end gap-3 py-4 px-5 border-t border-border-default', className)}
      {...rest}
    >
      {children}
    </div>
  );
};

export const DialogContent = ({
  children,
  className,
  ref,
  ...rest
}: {
  children: React.ReactNode;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}) => {
  return (
    <div ref={ref} className={cn('px-5 py-6 overflow-y-auto', className)} {...rest}>
      {children}
    </div>
  );
};

/**
 * An info strip hugging the dialog's bottom edge
 */
export const DialogFootnote = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return (
    <div
      data-slot="dialog-footnote"
      className={cn(
        'flex shrink-0 items-center justify-center gap-2 rounded-b-[19px] bg-blue-1 px-5 py-2 font-body-xs font-medium text-blue-9',
        className,
      )}
    >
      {children}
    </div>
  );
};

export const DialogLayout = ({ children, className, asChild = false, ...props }: DialogContentProps) => {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      className={cn(
        'flex flex-col bg-bg-sur-primary shadow-lg border border-border-default rounded-[20px] max-h-[calc(100vh-64px)]',
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  );
};
